import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Wallet, Crown, AlertTriangle, Clock, CheckCircle, CreditCard, 
  RefreshCw, ArrowUpRight, ArrowDownRight, Calendar, Sparkles
} from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export const AdminWalletManagement = () => {
  const { user, setUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rechargeAmount, setRechargeAmount] = useState(1000);
  const [recharging, setRecharging] = useState(false);
  const [showSubscribeDialog, setShowSubscribeDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("yearly");
  const [purchasing, setPurchasing] = useState(false);
  const [showRechargeConfirm, setShowRechargeConfirm] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchWalletData();
    }
  }, [user?.id]);

  const fetchWalletData = async () => {
    try {
      const [subRes, transRes] = await Promise.all([
        axios.get(`${API}/admin/${user.id}/subscription`),
        axios.get(`${API}/admin/${user.id}/wallet-transactions`).catch(() => ({ data: [] }))
      ]);
      setSubscription(subRes.data);
      setTransactions(transRes.data || []);
    } catch (err) {
      toast.error("Failed to load wallet data");
    } finally {
      setLoading(false);
    }
  };

  const handleRecharge = async () => {
    if (rechargeAmount < 100) {
      toast.error("Minimum recharge is ₹100");
      return;
    }
    setRecharging(true);
    try {
      const res = await axios.post(`${API}/admin/${user.id}/wallet/recharge?amount=${rechargeAmount}`);
      toast.success(res.data.message);
      
      // Update local user state
      if (setUser && user) {
        setUser({ ...user, wallet_balance: res.data.new_balance });
      }
      
      fetchWalletData();
      setRechargeAmount(1000);
      setShowRechargeConfirm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to recharge wallet");
    } finally {
      setRecharging(false);
    }
  };

  const handlePurchaseSubscription = async () => {
    setPurchasing(true);
    try {
      const res = await axios.post(`${API}/admin/${user.id}/subscription/purchase?plan=${selectedPlan}`);
      toast.success(res.data.message);
      fetchWalletData();
      setShowSubscribeDialog(false);
      
      // Trigger storage event to notify TrialRibbon to refresh
      localStorage.setItem(`subscription_updated_${user.id}`, Date.now().toString());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to purchase subscription");
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const plans = subscription?.pricing ? [
    { id: "monthly", label: "Monthly", price: subscription.pricing.monthly, period: "month", days: 30 },
    { id: "quarterly", label: "Quarterly", price: subscription.pricing.quarterly, period: "3 months", days: 90, savings: "17%" },
    { id: "half_yearly", label: "Half Yearly", price: subscription.pricing.half_yearly, period: "6 months", days: 180, savings: "25%" },
    { id: "yearly", label: "Yearly", price: subscription.pricing.yearly, period: "year", days: 365, savings: "33%", recommended: true },
  ] : [];

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-IN', { 
      day: 'numeric', month: 'short', year: 'numeric' 
    });
  };

  return (
    <div data-testid="admin-wallet-management">
      <h1 className="text-2xl font-bold text-foreground mb-6">Wallet & Subscription</h1>

      {/* Balance Warnings */}
      {subscription?.zero_balance_warning && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <div>
              <p className="font-semibold text-red-800">Wallet Balance is Zero!</p>
              <p className="text-sm text-red-600">OTPs will not work until you recharge your wallet. Please add funds immediately.</p>
            </div>
          </div>
        </div>
      )}

      {subscription?.low_balance_warning && !subscription?.zero_balance_warning && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <div>
              <p className="font-semibold text-amber-800">Low Wallet Balance</p>
              <p className="text-sm text-amber-600">Your balance is below ₹500. Consider recharging to avoid service interruption.</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Wallet Card */}
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Wallet Balance</h3>
                <p className="text-xs text-muted-foreground">OTP Cost: ₹{subscription?.otp_price?.toFixed(2) || "0.50"}/SMS</p>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={fetchWalletData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="text-4xl font-bold text-foreground mb-4">
            ₹{(subscription?.wallet_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </div>

          <div className="flex gap-2">
            {[500, 1000, 2000, 5000].map((amt) => (
              <Button
                key={amt}
                variant={rechargeAmount === amt ? "default" : "outline"}
                size="sm"
                onClick={() => setRechargeAmount(amt)}
              >
                ₹{amt}
              </Button>
            ))}
          </div>

          <div className="flex gap-2 mt-4">
            <Input
              type="number"
              min="100"
              value={rechargeAmount}
              onChange={(e) => setRechargeAmount(parseInt(e.target.value) || 0)}
              className="w-32"
            />
            <Button 
              onClick={() => setShowRechargeConfirm(true)}
              disabled={rechargeAmount < 100}
              className="flex-1"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Recharge Wallet
            </Button>
          </div>
        </div>

        {/* Subscription Card */}
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                subscription?.subscription_status === 'active' 
                  ? 'bg-green-100' 
                  : subscription?.is_trial 
                  ? 'bg-blue-100' 
                  : 'bg-red-100'
              }`}>
                <Crown className={`w-6 h-6 ${
                  subscription?.subscription_status === 'active' 
                    ? 'text-green-600' 
                    : subscription?.is_trial 
                    ? 'text-blue-600' 
                    : 'text-red-600'
                }`} />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Subscription Status</h3>
                <p className="text-xs text-muted-foreground">
                  {subscription?.subscription_status === 'active' ? 'Active subscription' : 
                   subscription?.is_trial ? 'Trial period' : 'No active subscription'}
                </p>
              </div>
            </div>
          </div>

          {subscription?.subscription_status === 'active' ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-lg font-bold text-green-600">
                  {subscription?.subscription_plan?.replace('_', ' ').replace(/^\w/, c => c.toUpperCase())} Plan
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground">Amount Paid</p>
                  <p className="font-semibold">₹{subscription?.subscription_amount_paid?.toLocaleString() || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Started</p>
                  <p className="font-semibold">{formatDate(subscription?.subscription_starts_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Expires</p>
                  <p className="font-semibold">{formatDate(subscription?.subscription_ends_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Days Remaining</p>
                  <p className="font-semibold text-green-600">{subscription?.subscription_days_remaining || 0} days</p>
                </div>
              </div>

              {subscription?.subscription_days_remaining <= 30 && (
                <Button onClick={() => setShowSubscribeDialog(true)} className="w-full">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Renew Subscription
                </Button>
              )}
            </div>
          ) : subscription?.is_trial ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-500" />
                <span className="text-lg font-bold text-blue-600">Trial Active</span>
              </div>
              
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  Trial expires on <strong>{formatDate(subscription?.trial_ends_at)}</strong>
                </p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {subscription?.trial_days_remaining} days remaining
                </p>
              </div>

              <Button onClick={() => setShowSubscribeDialog(true)} className="w-full">
                <Crown className="w-4 h-4 mr-2" />
                Upgrade Now
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <span className="text-lg font-bold text-red-600">
                  {subscription?.trial_expired ? 'Trial Expired' : 'No Active Subscription'}
                </span>
              </div>
              
              <p className="text-sm text-muted-foreground">
                Subscribe to continue using all features.
              </p>

              <Button onClick={() => setShowSubscribeDialog(true)} className="w-full" variant="destructive">
                <Crown className="w-4 h-4 mr-2" />
                Subscribe Now
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Transactions */}
      <div className="bg-card rounded-xl border border-border">
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold text-foreground">Transaction History</h3>
          <p className="text-sm text-muted-foreground">All payments made to Super Admin</p>
        </div>
        
        {transactions.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Wallet className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No transactions yet</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((tx) => (
                <TableRow key={tx.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {tx.type === 'recharge' ? (
                        <ArrowDownRight className="w-4 h-4 text-green-500" />
                      ) : (
                        <ArrowUpRight className="w-4 h-4 text-purple-500" />
                      )}
                      <span className="capitalize">{tx.type}</span>
                    </div>
                  </TableCell>
                  <TableCell>{tx.description}</TableCell>
                  <TableCell className="font-semibold">₹{tx.amount?.toLocaleString()}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded text-xs ${
                      tx.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {tx.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(tx.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Recharge Confirmation Dialog */}
      <AlertDialog open={showRechargeConfirm} onOpenChange={setShowRechargeConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Wallet Recharge</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to recharge your wallet with <strong>₹{rechargeAmount.toLocaleString()}</strong>.
              This amount will be added to your wallet balance for OTP charges.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRecharge} disabled={recharging}>
              {recharging ? "Processing..." : `Pay ₹${rechargeAmount.toLocaleString()}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Subscribe Dialog */}
      <Dialog open={showSubscribeDialog} onOpenChange={setShowSubscribeDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              Choose Subscription Plan
            </DialogTitle>
            <DialogDescription>
              Select a plan that works best for your business
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 py-4">
            {plans.map((plan) => (
              <button
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                  selectedPlan === plan.id
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
              >
                {plan.recommended && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                    Best Value
                  </span>
                )}
                <p className="font-semibold text-sm">{plan.label}</p>
                <p className="text-2xl font-bold mt-1">₹{plan.price?.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">per {plan.period}</p>
                {plan.savings && (
                  <p className="text-xs text-green-600 font-medium mt-1">Save {plan.savings}</p>
                )}
              </button>
            ))}
          </div>

          <div className="bg-muted p-4 rounded-lg">
            <h4 className="font-semibold mb-2">What's included:</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Unlimited drivers and customers</li>
              <li>• Real-time ride tracking</li>
              <li>• Commission management</li>
              <li>• City-based pricing</li>
              <li>• Sub-admin accounts</li>
              <li>• Priority support</li>
            </ul>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubscribeDialog(false)}>
              Maybe Later
            </Button>
            <Button onClick={handlePurchaseSubscription} disabled={purchasing}>
              {purchasing ? "Processing..." : `Pay ₹${plans.find(p => p.id === selectedPlan)?.price?.toLocaleString() || ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminWalletManagement;
